<template>

    <div>

        <v-combobox
            v-if="!categoriesLoaded"
            v-model="model"
            :filter="filter"
            :hide-no-data="!search"
            :items="categories"
            :search-input.sync="search"
            hide-selected
            :label="label"
            multiple
            clearable
            :hint="hint"
            persistent-hint
            :placeholder="placeHolder"
            item-text="name"
            item-value="id"
            :error-messages="errorMessages"
            @change="emitChanges"
        >
            <template v-slot:no-data>
                <v-list-item class="pa-0 ma-0">

                    <v-chip class="mr-2"
                            color="blue"
                            label
                            small
                            text-color="white"
                    >
                        {{ search }}
                    </v-chip>
                    <span class="caption">(ENTER to create)</span>
                </v-list-item>
            </template>

            <!-- SELECTED ITEMS -->
            <template
                slot="selection"
                v-if="item === Object(item)"
                slot-scope="{ item, parent, selected }"
            >
                <v-chip pill color="blue darken-1" text-color="white" :input-value="selected" label small>
                    <span class="pr-2">
                      {{ item.name }}
                    </span>
                    <v-avatar right>
                        <v-icon small @click="parent.selectItem(item)">mdi-close</v-icon>
                    </v-avatar>
                </v-chip>
            </template>

            <!-- EDIT CATEGORY -->
            <template
                slot="item"
                slot-scope="{ index, item, parent }"
            >
                <v-list-item-content class="pt-0 pb-0">

                    <v-text-field
                        v-if="editing === item"
                        v-model="editing.name"
                        autofocus
                        text
                        background-color="transparent"
                        hide-details
                        solo
                        @keyup.enter="edit(index, item)"
                    ></v-text-field>

                    {{ item.name }}
                </v-list-item-content>

                <v-spacer></v-spacer>

                <v-list-item-action @click.stop>
                    <span>

                        <v-btn v-if="editing === item" color="primary" small @click.stop.prevent="edit(index, item)">
                            UPDATE
                        </v-btn>

                        <v-btn v-show="!editing" icon small @click.stop.prevent="edit(index, item)">
                            <v-icon small>mdi-pencil</v-icon>
                        </v-btn>

                        <v-btn v-if="!editing"
                               icon
                               small
                               @click.stop.prevent="deleteItem(index, item)"
                        >
                            <v-icon small>mdi-delete</v-icon>
                        </v-btn>

                    </span>

                </v-list-item-action>

            </template>
        </v-combobox>

        <confirmation-dialog
            headline="Delete category?"
            description="Are you sure you want to delete this category?"
            confirm-button-text="Yes"
            abort-button-text="Cancel"
            delete
            v-model="deleteCategoryDialog"
            @selected="deleteConfirm"
        ></confirmation-dialog>

    </div>
</template>

<script>

import Store from '../store';
import ConfirmationDialog from "../../../components/UI/ConfirmationDialog";
import {mapState, mapMutations, mapGetters, mapActions} from 'vuex';

export default {

    components: {'confirmation-dialog': ConfirmationDialog},

    props: {
        value: {
            required: false,
            type: Array,
            default: () => []
        },
        label: {
            required: false,
            type: String,
            default: 'Selected categories'
        },
        placeHolder: {
            required: false,
            type: String,
            default: null
        },
        errorMessages: {
            required: false,
            type: Array,
        },
        hint: {
            required: false,
            type: String,
            default: "Select, search or create new categories"
        }
    },

    data: () => ({
        deleteCategoryDialog: false,
        itemToDelete: null,
        activator: null,
        attach: null,
        editing: null,
        index: -1,
        menu: false,
        model: [],
        search: null,
        oldSearch: null,
    }),

    watch: {
        model(val, prev) {
            if (val.length === prev.length) return;

            this.model = val.map(v => {

                // If creating a new category
                if (typeof v === 'string') {

                    v = {
                        id: -1,
                        name: v,
                    }

                    this.$store.dispatch('ProjectManager/createCategory', v)
                        .then((response) => {

                            Bus.$emit('flash-message', {message: 'Category created...'});

                            // Keep selected items model up to date
                            v.id = response.data.category.id;

                            // Clear search prompt if a new category is created
                            this.search = '';

                            // Send changes also to the parent
                            this.emitChanges();
                        })
                        .catch(({response}) => {

                            let index = this.model.findIndex((item) => {
                                return item.id === -1;
                            });

                            this.model.splice(index, 1);

                            // Restore old search in case of failure
                            this.search = this.oldSearch;

                            if (response.status === 409) {
                                Bus.$emit('flash-message', {
                                    type: 'error',
                                    message: 'Duplicate category name!'
                                });
                            } else if (response.status === 422) {
                                Bus.$emit('flash-message', {
                                    type: 'error',
                                    message: 'Category name must be at least 5 characters!'
                                });
                            } else {
                                Bus.$emit('server-error', {message: 'Category could not be created'});
                            }
                        });
                }

                return v;
            })
        },
    },

    computed: {

        ...mapState('ProjectManager', [
            'categories',
            'categoriesCacheExpires'
        ]),

        categoriesLoaded() {
            return !this.categoriesCacheExpires;
        },
    },

    methods: {

        ...mapActions('ProjectManager', [
            'loadCategories',
            'createCategory',
            'updateCategory',
            'deleteCategory'
        ]),

        edit(index, item) {

            if (!this.editing) {
                this.editing = item
                this.index = index
            } else {
                this.editing = null
                this.index = -1

                this.updateCategory(item)
                    .then((response) => {
                        Bus.$emit('flash-message', {message: 'Category updated...'});
                    })
                    .catch(({response}) => {

                        if (response.status === 409) {
                            Bus.$emit('flash-message', {
                                type: 'error',
                                message: 'Duplicate category name!'
                            });
                        } else {
                            Bus.$emit('server-error', {message: 'Category could not be updated'});
                        }
                    });
            }
        },

        filter(item, queryText, itemText) {

            const hasValue = val => val != null ? val : ''

            const name = hasValue(itemText)
            const query = hasValue(queryText)

            return name.toString()
                .toLowerCase()
                .indexOf(query.toString().toLowerCase()) > -1
        },

        deleteItem(index, item) {
            this.deleteCategoryDialog = true;
            this.itemToDelete = item;
        },

        deleteConfirm() {
            this.deleteCategoryDialog = false;

            this.deleteCategory(this.itemToDelete)
                .then((response) => {
                    Bus.$emit('flash-message', {message: 'Category deleted...'});
                })
                .catch(({response}) => {

                    if (response.status === 409 || response.status === 404) {
                        Bus.$emit('flash-message', {type: 'warning', message: response.data.message});
                    } else {
                        Bus.$emit('server-error', {message: 'OOPS! Category failed to delete!'});
                    }
                });
        },

        emitChanges() {

            // Save old search so it can be restored if category create operation fails
            this.oldSearch = this.search;

            // Filter out empty items
            this.model = this.model.filter(item => !(typeof item === "string" && item.length === 0));

            this.$emit("input", this.model);
        },
    },

    mounted() {
        this.loadCategories()
            .then(() => {

                if (this.value.length > 0) {

                    // If objects are supplied copy straight to the model
                    if (typeof this.value[0] === 'object') {
                        this.model = this.value.slice();
                    }

                    // If IDs are provided find objects and place into array
                    else {
                        this.categories.forEach((category) => {

                            let found = this.value.find((id) => {
                                return id === category.id
                            });

                            if (found) {
                                this.model.push(category);
                            }
                        })
                    }
                }
            })
            .catch(() => {
                Bus.$emit('server-error', {message: 'Categories could not be loaded'});
            });
    },

    created() {
        if (!this.$store.state.ProjectManager) {
            this.$store.registerModule('ProjectManager', Store);
        }
    },
}
</script>
